import React, { ReactNode } from "react";
import styled, { css } from "styled-components";

/**
 * SOREMO 共通メディアラベル。
 *
 * 目的: **補足情報をワードで意味を添える**。定型語彙をクリックだけで
 * 付与する入力支援（テキスト入力はしない）。
 *  - メディアアイテムの候補提出（プロデューサー側のおすすめラベル:
 *    オススメ / 調整前提 / 別案）
 *
 * タグ系コンポーネントの使い分けは `FilterChip` のコメントを参照。
 *
 * 視覚仕様:
 *  - StatusBadge の「旧環境」chip と同じジオメトリ
 *    （4px 角丸・padding 4px 8px・8px フォント）
 *  - 色はラベルの意味ごとに `tone` で指定:
 *    - `blue`    … soremo-blue 地 + 白文字（例: オススメ）
 *    - `gray`    … soremo-gray 地 + 白文字（例: 別案）
 *    - `outline` … background 地 + border 枠 + gray 文字（例: 調整前提）
 *    - `muted`   … background 地 + light-gray 文字（未選択の候補）
 *  - `onClick` を渡すと `<button>` 化してクリックで付与 / 解除できる
 */
export type MediaLabelTone = "blue" | "gray" | "outline" | "muted";
export type MediaLabelSize = "small" | "medium";

export interface MediaLabelProps {
  children: ReactNode;
  /** カラートーン。既定 "muted"（未選択の候補表示）。 */
  tone?: MediaLabelTone;
  /** サイズ。`small`=8px（既定・「旧環境」chip と同じ）、`medium`=11px。 */
  size?: MediaLabelSize;
  /**
   * クリック可能にする（`<button>` 化）。イベントを受けるので、カード内
   * 配置では `stopPropagation` できる。
   */
  // eslint-disable-next-line no-unused-vars
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
}

const sizeMap: Record<MediaLabelSize, string> = {
  small: "8px",
  medium: "11px",
};

const toneStyles = (tone: MediaLabelTone) => {
  switch (tone) {
    case "blue":
      return css`
        color: #fff;
        background-color: var(--soremo-blue);
        border-color: transparent;
      `;
    case "gray":
      return css`
        color: #fff;
        background-color: var(--soremo-gray);
        border-color: transparent;
      `;
    case "outline":
      return css`
        color: var(--soremo-gray);
        background-color: var(--soremo-background);
        border-color: var(--soremo-border);
      `;
    case "muted":
    default:
      return css`
        color: var(--soremo-light-gray);
        background-color: var(--soremo-background);
        border-color: transparent;
      `;
  }
};

const StyledLabel = styled.span<{
  $tone: MediaLabelTone;
  $size: MediaLabelSize;
  $clickable: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: fit-content;
  font-size: ${({ $size }) => sizeMap[$size]};
  line-height: 1;
  border-radius: 4px;
  border: 1px solid transparent;
  padding: 4px 8px;
  white-space: nowrap;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  ${({ $tone }) => toneStyles($tone)}

  /* button としての見た目リセット */
  appearance: none;
  margin: 0;
  outline: none;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};

  ${({ $clickable, $tone }) =>
    $clickable &&
    $tone === "muted" &&
    css`
      /* 未選択候補のホバー: 文字色を一段濃くしてクリック可能を示す */
      &:hover {
        color: var(--soremo-gray);
      }
    `}

  &:focus-visible {
    outline: 2px solid var(--soremo-blue);
    outline-offset: 2px;
  }
`;

const MediaLabel: React.FC<MediaLabelProps> = ({
  children,
  tone = "muted",
  size = "small",
  onClick,
  className,
}) => {
  const clickable = !!onClick;
  return (
    <StyledLabel
      as={clickable ? "button" : "span"}
      type={clickable ? "button" : undefined}
      onClick={onClick}
      $tone={tone}
      $size={size}
      $clickable={clickable}
      className={className}
    >
      {children}
    </StyledLabel>
  );
};

export default MediaLabel;
